import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Paper,
    Typography,
    Chip,
    OutlinedInput,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
} from '@mui/material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import Papa, { ParseResult } from 'papaparse';
import { getSubscriptionTrendsCSV } from '../organizations.api';

interface SubscriptionData {
    month: string;
    product_name: string;
    price_id: string;
    total_quantity: number;
    total_revenue_usd: number;
}

interface AggregatedData {
    month: string;
    [key: string]: string | number;
}

type ChartType = 'line' | 'bar' | 'area';

const COLORS = [
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff7c7c',
    '#8dd1e1',
    '#d084d0',
    '#a4de6c',
    '#d0ed57',
    '#ffa07a',
    '#20b2aa',
];

// Product display name mappings
const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
    Fiyrpod: 'Organization Fiyrpod',
    'FiyrLink - Laptop Gateway - Up to 48 Units': 'Fiyrlink',
    'FiyrLink - Laptop Gateway - Up to 48 Units (FREE)': 'Fiyrlink (FREE)',
};

// Custom sort order for products
const PRODUCT_SORT_ORDER: Record<string, number> = {
    'Organization Fiyrpod': 1,
    'Org Subscription - Gold Package': 2,
    'Org Subscription - Silver Package': 3,
    'Org Subscription - Bronze Package': 4,
};

// Helper function to get display name
const getProductDisplayName = (productName: string): string => {
    // First, remove "with Phone Cradle" suffix if present
    let cleanedName = productName.replace(/\s*with Phone Cradle\s*/gi, '').trim();

    // Then apply custom display name mapping
    return PRODUCT_DISPLAY_NAMES[cleanedName] || cleanedName;
};

// Helper function to get sort priority
const getProductSortPriority = (productName: string): number => {
    const displayName = getProductDisplayName(productName);
    return PRODUCT_SORT_ORDER[displayName] || 999;
};

const SubscriptionTrends: React.FC = () => {
    const [rawData, setRawData] = useState<SubscriptionData[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
    const [startMonth, setStartMonth] = useState<string>('');
    const [endMonth, setEndMonth] = useState<string>('');
    const [chartType, setChartType] = useState<ChartType>('line');
    const [uploadError, setUploadError] = useState<string>('');
    const [hasSetDefaultDates, setHasSetDefaultDates] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [lastUpdated, setLastUpdated] = useState<string>('');

    // Get unique products from data with custom sorting
    const availableProducts = useMemo(() => {
        const products = new Set(rawData.map((d) => d.product_name));
        return Array.from(products).sort((a, b) => {
            const priorityA = getProductSortPriority(a);
            const priorityB = getProductSortPriority(b);

            // Sort by priority first
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // If same priority (both 999), sort alphabetically by display name
            return getProductDisplayName(a).localeCompare(getProductDisplayName(b));
        });
    }, [rawData]);

    // Calculate immutable totals (all data)
    const immutableTotals = useMemo(() => {
        return {
            totalQuantity: rawData.reduce((sum, row) => sum + row.total_quantity, 0),
            totalRevenue: rawData.reduce((sum, row) => sum + row.total_revenue_usd, 0),
        };
    }, [rawData]);

    // Filter data based on selections
    const filteredData = useMemo(() => {
        let filtered = [...rawData];

        if (selectedProducts.length > 0) {
            filtered = filtered.filter((row) => selectedProducts.includes(row.product_name));
        }

        // Only apply date filters if they are explicitly set
        // Convert both to YYYY-MM format for proper comparison
        if (startMonth) {
            filtered = filtered.filter((row) => {
                const rowMonth = row.month.substring(0, 7); // Convert "2025-10-01" to "2025-10"
                return rowMonth >= startMonth;
            });
        }

        if (endMonth) {
            filtered = filtered.filter((row) => {
                const rowMonth = row.month.substring(0, 7); // Convert "2025-10-01" to "2025-10"
                return rowMonth <= endMonth;
            });
        }

        return filtered;
    }, [rawData, selectedProducts, startMonth, endMonth]);

    // Calculate filtered totals
    const filteredTotals = useMemo(() => {
        return {
            totalQuantity: filteredData.reduce((sum, row) => sum + row.total_quantity, 0),
            totalRevenue: filteredData.reduce((sum, row) => sum + row.total_revenue_usd, 0),
        };
    }, [filteredData]);

    // Aggregate data for charts
    const chartData = useMemo(() => {
        const aggregated = new Map<string, AggregatedData>();

        filteredData.forEach((row) => {
            const monthKey = row.month.substring(0, 7);
            const existing = aggregated.get(monthKey) || { month: monthKey };
            const displayName = getProductDisplayName(row.product_name);

            if (selectedProducts.length > 0) {
                existing[`${displayName}_quantity`] =
                    ((existing[`${displayName}_quantity`] as number) || 0) + row.total_quantity;
                existing[`${displayName}_revenue`] =
                    ((existing[`${displayName}_revenue`] as number) || 0) + row.total_revenue_usd;
            } else {
                existing['total_quantity'] =
                    ((existing['total_quantity'] as number) || 0) + row.total_quantity;
                existing['total_revenue'] =
                    ((existing['total_revenue'] as number) || 0) + row.total_revenue_usd;
            }

            aggregated.set(monthKey, existing);
        });

        const result = Array.from(aggregated.values()).sort((a, b) =>
            a.month.localeCompare(b.month),
        );

        // Fill in missing months with 0 values for selected products
        if (selectedProducts.length > 0 && result.length > 0) {
            const filledResult: AggregatedData[] = [];

            for (const monthData of result) {
                const filled: AggregatedData = { month: monthData.month };

                // Ensure all selected products have values for this month
                selectedProducts.forEach((product) => {
                    const displayName = getProductDisplayName(product);
                    filled[`${displayName}_quantity`] =
                        (monthData[`${displayName}_quantity`] as number) || 0;
                    filled[`${displayName}_revenue`] =
                        (monthData[`${displayName}_revenue`] as number) || 0;
                });

                filledResult.push(filled);
            }

            return filledResult;
        }

        return result;
    }, [filteredData, selectedProducts]);

    // Load CSV data on component mount
    useEffect(() => {
        const loadCSVData = async () => {
            setIsLoading(true);
            setUploadError('');

            try {
                const response = await getSubscriptionTrendsCSV();

                // The response now contains the CSV content directly as text
                const csvText = response.data;

                // Extract last modified date from response headers if available
                const lastModified = response.headers['last-modified'];
                if (lastModified) {
                    setLastUpdated(lastModified);
                }

                // Parse the CSV
                Papa.parse(csvText, {
                    header: true,
                    dynamicTyping: true,
                    skipEmptyLines: true,
                    complete: (results: ParseResult<SubscriptionData>) => {
                        try {
                            const parsed = results.data as any[];
                            const validated: SubscriptionData[] = parsed
                                .filter((row) => row.month && row.product_name)
                                .map((row) => ({
                                    month: row.month,
                                    product_name: row.product_name,
                                    price_id: row.price_id || '',
                                    total_quantity: Number(row.total_quantity) || 0,
                                    total_revenue_usd: Number(row.total_revenue_usd) || 0,
                                }));

                            if (validated.length === 0) {
                                setUploadError('No valid data found in CSV file');
                                return;
                            }

                            setRawData(validated);
                        } catch (error) {
                            setUploadError('Error parsing CSV file. Please check the format.');
                        } finally {
                            setIsLoading(false);
                        }
                    },
                    error: () => {
                        setUploadError('Failed to parse CSV file');
                        setIsLoading(false);
                    },
                });
            } catch (error: any) {
                console.error('Error loading subscription trends:', error);

                // Handle 404 error specifically
                if (error.response?.status === 404) {
                    setUploadError(
                        error.response?.data?.error ||
                            'Subscription trends file not found. The file may not have been generated yet.',
                    );
                } else {
                    setUploadError(
                        error.response?.data?.error ||
                            'Failed to load subscription trends data. Please try again later.',
                    );
                }
                setIsLoading(false);
            }
        };

        void loadCSVData();
    }, []);

    // Set default date range (last 6 months) when data is first loaded
    useEffect(() => {
        if (rawData.length > 0 && !hasSetDefaultDates) {
            const months = Array.from(new Set(rawData.map((d) => d.month))).sort();
            if (months.length > 0) {
                const latestMonth = months[months.length - 1];
                const sixMonthsAgo = months[Math.max(0, months.length - 6)];

                // Convert YYYY-MM-DD to YYYY-MM format for month input
                const startMonthFormatted = sixMonthsAgo.substring(0, 7);
                const endMonthFormatted = latestMonth.substring(0, 7);

                setStartMonth(startMonthFormatted);
                setEndMonth(endMonthFormatted);
                setHasSetDefaultDates(true);
            }
        }
    }, [rawData, hasSetDefaultDates]);

    const handleProductChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedProducts(typeof value === 'string' ? value.split(',') : value);
    };

    const handleChartTypeChange = (
        _event: React.MouseEvent<HTMLElement>,
        newType: ChartType | null,
    ) => {
        if (newType !== null) {
            setChartType(newType);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(value);
    };

    const formatNumber = (value: number) => {
        return new Intl.NumberFormat('en-US').format(value);
    };

    const renderChart = () => {
        if (chartData.length === 0) {
            return (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        No data to display. Please adjust your filters.
                    </Typography>
                </Box>
            );
        }

        const dataKeys = Object.keys(chartData[0]).filter((key) => key !== 'month');
        const revenueKeys = dataKeys.filter(
            (key) => key.includes('revenue') || key === 'total_revenue',
        );
        const quantityKeys = dataKeys.filter(
            (key) => key.includes('quantity') || key === 'total_quantity',
        );

        const commonProps = {
            data: chartData,
            margin: { top: 5, right: 30, left: 20, bottom: 5 },
        };

        return (
            <Box>
                {/* Revenue Chart */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Revenue Trends (USD)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    {chartType === 'line' ? (
                        <LineChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            <Legend />
                            {revenueKeys.map((key, index) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={COLORS[index % COLORS.length]}
                                    name={key.replace('_revenue', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </LineChart>
                    ) : chartType === 'bar' ? (
                        <BarChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            <Legend />
                            {revenueKeys.map((key, index) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    fill={COLORS[index % COLORS.length]}
                                    name={key.replace('_revenue', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </BarChart>
                    ) : (
                        <AreaChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            <Legend />
                            {revenueKeys.map((key, index) => (
                                <Area
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stackId="1"
                                    stroke={COLORS[index % COLORS.length]}
                                    fill={COLORS[index % COLORS.length]}
                                    name={key.replace('_revenue', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </AreaChart>
                    )}
                </ResponsiveContainer>

                {/* Quantity Chart */}
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                    Quantity Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                    {chartType === 'line' ? (
                        <LineChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatNumber(value)} />
                            <Legend />
                            {quantityKeys.map((key, index) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stroke={COLORS[index % COLORS.length]}
                                    name={key.replace('_quantity', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </LineChart>
                    ) : chartType === 'bar' ? (
                        <BarChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatNumber(value)} />
                            <Legend />
                            {quantityKeys.map((key, index) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    fill={COLORS[index % COLORS.length]}
                                    name={key.replace('_quantity', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </BarChart>
                    ) : (
                        <AreaChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value: number) => formatNumber(value)} />
                            <Legend />
                            {quantityKeys.map((key, index) => (
                                <Area
                                    key={key}
                                    type="monotone"
                                    dataKey={key}
                                    stackId="1"
                                    stroke={COLORS[index % COLORS.length]}
                                    fill={COLORS[index % COLORS.length]}
                                    name={key.replace('_quantity', '').replace(/_/g, ' ')}
                                />
                            ))}
                        </AreaChart>
                    )}
                </ResponsiveContainer>
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Subscription revenue and quantity trends over time. This data includes all line
                items from subscriptions only, not one-off purchases.
            </Typography>

            {lastUpdated && (
                <Typography
                    variant="caption"
                    color="#AAA"
                    sx={{ mb: 3, display: 'block', fontSize: '0.7rem', fontStyle: 'italic' }}
                >
                    Last updated: {lastUpdated}
                </Typography>
            )}

            {isLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '200px',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {uploadError && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {uploadError}
                </Alert>
            )}

            {!isLoading && rawData.length > 0 && (
                <>
                    {/* Summary Statistics - Compact 2x2 Grid */}
                    <Box
                        sx={{
                            p: 2,
                            mb: 3,
                            maxWidth: 'fit-content',
                            border: '1px solid',
                            borderColor: 'grey.300',
                            borderRadius: '4px',
                        }}
                    >
                        {/* All-Time Row */}
                        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    All-Time Revenue
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {formatCurrency(immutableTotals.totalRevenue)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    All-Time Quantity
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {formatNumber(immutableTotals.totalQuantity)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Filtered Row */}
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Filtered Revenue
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="primary.main"
                                >
                                    {formatCurrency(filteredTotals.totalRevenue)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    Filtered Quantity
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    color="primary.main"
                                >
                                    {formatNumber(filteredTotals.totalQuantity)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Filters */}
                    <Box sx={{ mt: 10, mb: 6 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControl sx={{ minWidth: 300 }}>
                                <InputLabel size="small">Products</InputLabel>
                                <Select
                                    multiple
                                    size="small"
                                    value={selectedProducts}
                                    onChange={handleProductChange}
                                    input={<OutlinedInput label="Products" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={getProductDisplayName(value)}
                                                    size="small"
                                                />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {availableProducts.map((product) => (
                                        <MenuItem key={product} value={product}>
                                            {getProductDisplayName(product)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Start Month"
                                type="month"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={startMonth}
                                onChange={(e) => setStartMonth(e.target.value)}
                                sx={{ width: '180px' }}
                            />

                            <TextField
                                label="End Month"
                                type="month"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={endMonth}
                                onChange={(e) => setEndMonth(e.target.value)}
                                sx={{ width: '180px' }}
                            />

                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setSelectedProducts([]);
                                    setStartMonth('');
                                    setEndMonth('');
                                }}
                                size="small"
                            >
                                Clear Filters
                            </Button>
                        </Box>
                    </Box>

                    {/* Chart Type Toggle */}
                    <Box sx={{ mb: 2 }}>
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={handleChartTypeChange}
                            aria-label="chart type"
                        >
                            <ToggleButton value="line" aria-label="line chart">
                                Line Chart
                            </ToggleButton>
                            <ToggleButton value="bar" aria-label="bar chart">
                                Bar Chart
                            </ToggleButton>
                            <ToggleButton value="area" aria-label="area chart">
                                Stacked Area Chart
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Charts */}
                    <Paper sx={{ p: 3 }}>{renderChart()}</Paper>
                </>
            )}
        </Box>
    );
};

export default SubscriptionTrends;
